/*
Reading a number out of user data, without the coercion trap.

`Number("")` is `0`, and `0` is finite. So the natural-looking test

    Number.isFinite(Number(cell))

says a blank cell holds a valid number, and code downstream treats an empty
latitude as the equator. This repo has been bitten by that three times: the map
selector that dropped points with a zero coordinate (#190), the paste dialog's
"how many rows will land on the map" count, and the map-pan decision — each
caught by a test that failed on its first run, and each fixed locally with its
own private guard.

Four such guards existed when this file was written, and THREE of them did not
trim, so a cell containing only spaces still read as `0`. Pasted spreadsheet
data is full of those. That is the reason this lives in one place now: the bug
is not hard to fix, it is hard to remember, and a helper that is right once is
better than four that are right three times.

`toFiniteNumber` returns null rather than NaN on purpose. NaN propagates
silently through arithmetic and comparisons — `NaN > 90` is false, so a range
check quietly passes — while null forces the caller to decide.
*/

/** Blank means "nothing was entered": null, undefined, or only whitespace. */
export function isBlank(value) {
  return value === null || value === undefined || String(value).trim() === "";
}

/**
 * The value as a finite number, or null if it is blank or not numeric.
 * @returns {number|null}
 */
export function toFiniteNumber(value) {
  if (isBlank(value)) return null;
  // Number(true) is 1, which would let a checkbox column become a coordinate.
  // Same silent coercion as the empty string, one type over.
  if (typeof value === "boolean") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** True when the value holds a real number — blank is NOT a number. */
export function isFiniteNumeric(value) {
  return toFiniteNumber(value) !== null;
}
